import { useTranslation } from 'react-i18next';

/**
 * Moon phase indicator — visualises today's lunar phase as an SVG.
 *
 * Math: synodic month = 29.5305882 days. Reference new moon: 2000-01-06 18:14 UTC
 * (a known new moon). Number of synodic months since reference / 1 = fractional
 * phase (0 = new, 0.5 = full, etc.).
 *
 * Render: a circle clipped by an ellipse whose horizontal radius animates
 * with the phase. We compute it deterministically per date so SSR-safe.
 */
const SYNODIC = 29.5305882;
const REF_NEW_MOON = Date.UTC(2000, 0, 6, 18, 14) / 1000; // unix seconds

function phaseFraction(d: Date): number {
  const now = d.getTime() / 1000;
  const synodic = ((now - REF_NEW_MOON) / 86400) % SYNODIC;
  const positive = synodic < 0 ? synodic + SYNODIC : synodic;
  return positive / SYNODIC; // 0..1
}

interface PhaseInfo {
  fraction: number;     // 0..1 (0 = new, 0.5 = full)
  illumination: number; // 0..1
  waxing: boolean;
  name: { ru: string; en: string };
}

function describePhase(f: number): PhaseInfo {
  const ill = Math.sin(Math.PI * f) ** 2;
  const waxing = f < 0.5;
  let key: keyof typeof NAMES;
  if (f < 0.03 || f > 0.97) key = 'new';
  else if (f < 0.22) key = 'waxing_crescent';
  else if (f < 0.28) key = 'first_quarter';
  else if (f < 0.47) key = 'waxing_gibbous';
  else if (f < 0.53) key = 'full';
  else if (f < 0.72) key = 'waning_gibbous';
  else if (f < 0.78) key = 'last_quarter';
  else key = 'waning_crescent';
  return { fraction: f, illumination: ill, waxing, name: NAMES[key] };
}

const NAMES = {
  new:               { ru: 'Новолуние',         en: 'New moon' },
  waxing_crescent:   { ru: 'Растущий серп',     en: 'Waxing crescent' },
  first_quarter:     { ru: 'Первая четверть',   en: 'First quarter' },
  waxing_gibbous:    { ru: 'Растущая луна',     en: 'Waxing gibbous' },
  full:              { ru: 'Полнолуние',        en: 'Full moon' },
  waning_gibbous:    { ru: 'Убывающая луна',    en: 'Waning gibbous' },
  last_quarter:      { ru: 'Последняя четверть', en: 'Last quarter' },
  waning_crescent:   { ru: 'Убывающий серп',    en: 'Waning crescent' },
};


interface Props {
  size?: number;
  date?: Date;
}

export default function MoonPhase({ size = 72, date = new Date() }: Props) {
  const { i18n } = useTranslation();
  const lang = i18n.language.startsWith('ru') ? 'ru' : 'en';
  const phase = describePhase(phaseFraction(date));

  // Geometry (correct phase rendering):
  //
  // Draw a dark disk, then overlay a LIT shape bounded by two arcs:
  //   • half of the disk circle (right for waxing, left for waning)
  //   • the terminator ellipse, which always passes through the two
  //     poles of the moon (top and bottom) with horizontal radius
  //     |r·cos(phaseAngle)|
  //
  // The bigness of the lit area depends on which side of the terminator
  // ellipse we trace: same side as the disk arc (crescent) or opposite
  // side (gibbous).
  const r = size / 2;
  const cx = r;
  const cy = r;
  const phaseAngle = phase.fraction * 2 * Math.PI;
  const termRx = Math.max(0.01, Math.abs(r * Math.cos(phaseAngle)));

  // ±2% of the lunar cycle around full / new — narrow enough that we still
  // see crescents/gibbous correctly, wide enough to avoid degenerate SVG
  // arcs when termRx ≈ ry (where the half-ellipse arc can render flat).
  const isNearFull = Math.abs(phase.fraction - 0.5) < 0.02;
  const isNearNew = phase.fraction < 0.01 || phase.fraction > 0.99;

  // Disk arc direction: waxing draws the right half (sweep=1),
  // waning draws the left half (sweep=0).
  const diskSweep = phase.waxing ? 1 : 0;
  // Terminator arc direction (verified by walking through all 4 quadrants):
  //   • gibbous (>50% lit): SAME sweep as disk arc — both arcs go round
  //     the same direction, so the closed path encloses most of the disk
  //     (small dark crescent on the opposite side).
  //   • crescent (<50% lit): OPPOSITE sweep — the two arcs meet at the
  //     poles and pinch off a thin lit sliver on the lit side.
  const isGibbous = phase.illumination > 0.5;
  const termSweep = isGibbous ? diskSweep : (1 - diskSweep);

  const litPath =
    `M ${cx},${cy - (r - 0.5)} ` +
    `A ${r - 0.5},${r - 0.5} 0 0 ${diskSweep} ${cx},${cy + (r - 0.5)} ` +
    `A ${termRx},${r - 0.5} 0 0 ${termSweep} ${cx},${cy - (r - 0.5)} ` +
    `Z`;

  return (
    <div className="flex items-center gap-3">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <radialGradient id="moon-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#fff6c7" />
            <stop offset="70%" stopColor="#e8c356" />
            <stop offset="100%" stopColor="#a78320" />
          </radialGradient>
          <radialGradient id="moon-shadow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#1e1740" />
            <stop offset="100%" stopColor="#0b0820" />
          </radialGradient>
        </defs>

        {/* Soft outer glow */}
        <circle cx={cx} cy={cy} r={r} fill="url(#moon-glow)" opacity="0.18" />
        {/* Dark base disk (shadow side, always visible at new moon) */}
        <circle cx={cx} cy={cy} r={r - 0.5} fill="url(#moon-shadow)" />
        {/* Lit area, drawn ONLY when not new moon */}
        {!isNearNew && (
          isNearFull
            ? <circle cx={cx} cy={cy} r={r - 0.5} fill="url(#moon-glow)" />
            : <path d={litPath} fill="url(#moon-glow)" />
        )}
        {/* Outer ring for definition */}
        <circle cx={cx} cy={cy} r={r - 0.5} fill="none"
                stroke="#e8c356" strokeWidth="0.5" opacity="0.5" />
      </svg>
      <div className="leading-tight">
        <div className="text-sm text-gold-300">{phase.name[lang]}</div>
        <div className="text-xs text-gold-300/60">
          {Math.round(phase.illumination * 100)}%
        </div>
      </div>
    </div>
  );
}
